import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { Button } from './Button'

/* ------------------------------------------------------------------
   حالت‌های استاندارد صفحه — سند مادر بند ۱۱.۱
   «هر ماژول صفحه Landing و وضعیت‌های Empty/Error/Loading استاندارد دارد.»
   ------------------------------------------------------------------ */

export interface EmptyStateProps {
  title: string
  description?: ReactNode
  icon?: ReactNode
  /**
   * نشانی تصویر سه‌بعدی — جایگزین قرص آیکون خطی می‌شود.
   * ⚠️ عمداً نشانی می‌گیرد و خودش مسیر نمی‌سازد: ui-kit پکیج مشترک است
   * و نباید به فایل‌های داخل اپ وب گره بخورد.
   */
  illustration?: string
  action?: ReactNode
  className?: string
}

export function EmptyState({
  title,
  description,
  icon,
  illustration,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        'px-6 py-14 gap-3',
        className,
      )}
    >
      {illustration ? (
        <img
          src={illustration}
          alt=""
          aria-hidden="true"
          width={88}
          height={88}
          loading="lazy"
          decoding="async"
          className="size-22 object-contain"
        />
      ) : (
        <div className="flex size-12 items-center justify-center rounded-[var(--pf-radius-xl)] bg-[var(--pf-surface-muted)] text-[var(--pf-text-subtle)]">
          {icon ?? (
            <svg viewBox="0 0 24 24" fill="none" className="size-6" aria-hidden="true">
              <path
                d="M4 7h16M4 12h16M4 17h10"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          )}
        </div>
      )}

      <h3 className="text-[length:var(--pf-text-base)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)]">
        {title}
      </h3>

      {description && (
        <p className="max-w-sm text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">
          {description}
        </p>
      )}

      {action && <div className="mt-1">{action}</div>}
    </div>
  )
}

/* ------------------------------------------------------------------ */

export interface ErrorStateProps {
  title?: string
  description?: ReactNode
  /** شناسه درخواست از Envelope خطای بند ۱۵.۱ — برای پیگیری پشتیبانی */
  requestId?: string | null
  /** نشانی تصویر سه‌بعدی — مثل EmptyState، مسیر را اپ می‌دهد نه پکیج */
  illustration?: string
  onRetry?: () => void
  className?: string
}

export function ErrorState({
  title = 'خطایی رخ داد',
  description = 'در دریافت اطلاعات مشکلی پیش آمد. لطفاً دوباره تلاش کنید.',
  requestId,
  /* تصویر پیش‌فرض همان چیزی است که تقریباً همه‌جا دیده می‌شود: بیشتر
     صداکننده‌ها تصویری نمی‌دهند. نشان کوچک قرمز قبلی کنار بقیه حالت‌های
     خالی که رندر سه‌بعدی دارند، وصله ناجور بود. */
  illustration = '/icons/3d/error-server.png',
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center text-center px-6 py-14 gap-3',
        className,
      )}
    >
      {illustration ? (
        <img
          src={illustration}
          alt=""
          aria-hidden="true"
          width={88}
          height={88}
          loading="lazy"
          decoding="async"
          className="size-22 object-contain"
        />
      ) : (
        <div className="flex size-12 items-center justify-center rounded-[var(--pf-radius-xl)] bg-[var(--pf-danger-50)] text-[var(--pf-danger-text)]">
          <svg viewBox="0 0 24 24" fill="none" className="size-6" aria-hidden="true">
            <path
              d="M12 8v5m0 3.5h.01M10.3 3.9 2.4 17.5A1.9 1.9 0 0 0 4 20.4h16a1.9 1.9 0 0 0 1.6-2.9L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      <h3 className="text-[length:var(--pf-text-base)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)]">
        {title}
      </h3>

      <p className="max-w-md text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">{description}</p>

      {requestId && (
        <code
          dir="ltr"
          className="rounded-[var(--pf-radius-sm)] bg-[var(--pf-surface-muted)] px-2 py-1 text-[length:var(--pf-text-2xs)] text-[var(--pf-text-subtle)] font-[var(--pf-font-mono)]"
        >
          {requestId}
        </code>
      )}

      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry} className="mt-1">
          تلاش دوباره
        </Button>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */

export interface LoadingStateProps {
  label?: string
  className?: string
}

export function LoadingState({ label = 'در حال بارگذاری…', className }: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn('flex flex-col items-center justify-center px-6 py-14 gap-3', className)}
    >
      {/* --pf-link و نه --pf-primary: چرخنده روی سطح روشن کشیده می‌شود و
          --pf-primary (سبز روشنِ دکمه) آنجا محو است. */}
      <svg viewBox="0 0 24 24" fill="none" className="size-7 animate-spin text-[var(--pf-link)]" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2.5" opacity="0.2" />
        <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
      <p className="text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">{label}</p>
    </div>
  )
}

/* ------------------------------------------------------------------ */

export interface SkeletonProps {
  className?: string
  /** تعداد خطوط — برای اسکلت متنی */
  lines?: number
}

export function Skeleton({ className, lines }: SkeletonProps) {
  if (lines && lines > 1) {
    return (
      <div className="flex flex-col gap-2" aria-hidden="true">
        {Array.from({ length: lines }).map((_, index) => (
          <div
            key={index}
            className={cn(
              'h-3.5 animate-pulse rounded-[var(--pf-radius-sm)] bg-[var(--pf-surface-muted)]',
              index === lines - 1 && 'w-2/3',
              className,
            )}
          />
        ))}
      </div>
    )
  }

  return (
    <div
      aria-hidden="true"
      className={cn(
        'animate-pulse rounded-[var(--pf-radius)] bg-[var(--pf-surface-muted)]',
        'h-4 w-full',
        className,
      )}
    />
  )
}

/* ------------------------------------------------------------------
   حالت «دسترسی ندارید» — متمایز از خطا
   طبق بند ۱۱.۱: نبودن منو جایگزین کنترل Backend نیست، پس کاربر
   ممکن است با Deep Link به صفحه‌ای برسد که مجاز نیست.
   ------------------------------------------------------------------ */

export interface ForbiddenStateProps {
  description?: ReactNode
  className?: string
}

export function ForbiddenState({
  description = 'برای مشاهده این بخش دسترسی لازم را ندارید. در صورت نیاز با مدیر سامانه تماس بگیرید.',
  className,
}: ForbiddenStateProps) {
  return (
    <div className={cn('flex flex-col items-center justify-center text-center px-6 py-14 gap-3', className)}>
      <div className="flex size-12 items-center justify-center rounded-[var(--pf-radius-xl)] bg-[var(--pf-warning-50)] text-[var(--pf-warning-700)]">
        <svg viewBox="0 0 24 24" fill="none" className="size-6" aria-hidden="true">
          <path
            d="M7 10V7a5 5 0 0 1 10 0v3M5 10h14v10H5z"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <h3 className="text-[length:var(--pf-text-base)] font-[var(--pf-weight-semibold)] text-[var(--pf-text)]">
        دسترسی مجاز نیست
      </h3>
      <p className="max-w-md text-[length:var(--pf-text-sm)] text-[var(--pf-text-muted)]">{description}</p>
    </div>
  )
}
